import NetxQueryClient, {
	type SuccessResponse,
	type QueryPager,
} from '@netx/core-next/lib/queryClient';
import {getResponseResults} from '@netx/core-next/utils/api';
export type {QueryError} from '@netx/core-next/lib/queryClient';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';

import {
	getSharedResourceDefaultSort,
	getSharedResourceDefaultFilters,
	SharedResourceName,
	SharedResourceModeFilter,
	type ISharedResource,
	type SharedResourceFilter,
	type SharedResourceSort,
} from '../schema';

/**
 * `getSharedResources` request params
 * (what is used to send to server)
 */
export type GetSharedResourcesForUserRequestOptions = {
	userId?: number;
	filters?: Partial<SharedResourceFilter>;
	resourceName: SharedResourceName;
	modeFilter?: SharedResourceModeFilter;
	sort?: Partial<SharedResourceSort>;
	startIndex?: number;
	stopIndex?: number;
};

/**
 * `getSharedResources` request
 * (what gets sent to the server)
 */
export type GetSharedResourcesForUserRequest = [
	Required<GetSharedResourcesForUserRequestOptions>['userId'],
	Required<GetSharedResourcesForUserRequestOptions>['resourceName'],
	Required<GetSharedResourcesForUserRequestOptions>['modeFilter'] | null,
	// right now we just support the single string filter search on group name
	string,
	SharedResourceSort, // no sorting on server atm
	QueryPager,
];

/**
 * `getSharedResources` response
 * (what comes back from the server)
 */
export type GetSharedResourcesForUserResponse =
	SuccessResponse<GetSharedResourcesForUserResult>;

/**
 * `getSharedResources` result
 * (what is utilized from the server response)
 */
export type GetSharedResourcesForUserResult = {
	results: ISharedResource[];
	size: number;
	startIndex: number;
};

export const getSharedResourcesForUserEndpoint = 'getSharedResourcesForUser';

/**
 * Get shared resources
 * @param fetchParams Fetch params
 * @param signal Abort signal
 * @returns Promise of paged shared resources
 *
 * @example
 * ```ts
 * const sharedResource = await getSharedResources({
 *  resourceName: SharedResourceName.SAVED_CART,
 * 	filters: {
 * 		name: 'foo',
 * 	},
 * 	startIndex: 0,
 * 	stopIndex: 10,
 * });
 * ```
 */
export const getSharedResourcesForUser = async (
	{
		filters,
		modeFilter,
		resourceName,
		userId,
		sort,
		startIndex = 0,
		stopIndex = 25,
	}: GetSharedResourcesForUserRequestOptions,
	signal?: AbortSignal,
): Promise<GetSharedResourcesForUserResult> => {
	const client = NetxQueryClient();

	const requestParams: GetSharedResourcesForUserRequest = [
		userId ?? userProfile.id,
		resourceName,
		modeFilter ?? null,
		getSharedResourceDefaultFilters({filters}).title ?? '',
		getSharedResourceDefaultSort(sort),
		{
			startIndex,
			size: stopIndex - startIndex,
		},
	];

	return client
		.post<GetSharedResourcesForUserResponse>(
			getSharedResourcesForUserEndpoint,
			requestParams,
			{
				signal,
			},
		)
		.then((response) => getResponseResults(response));
};
